/**
 * PHARMAKON — Trésorerie · « Comptes & caisses » : the copy of C1-C6 (TWP-18).
 *
 * Verbatim French UI copy of design/ux/02_comptes_caisses_mouvements.md §4-§9 (the contract for E2E
 * locators). Sentence case; « DA » comes from the money formatter, never typed here; no amount is
 * computed here (03_UX_DESIGN P-5) — a sentence that names an amount receives it already formatted.
 */

export const COMPTES = Object.freeze({
  titre: "Comptes & caisses",
  sousTitre: "Où est l'argent de l'officine, compte par compte.",
  onglets: Object.freeze({
    comptes: "Comptes et caisses",
    sessions: "Sessions de caisse",
  }),
  ongletsLabel: "Vues des comptes",

  // ── KPI strip (UX 02 §4.2) ──
  tuiles: Object.freeze({
    liquidite: "Liquidité totale",
    banques: "Comptes bancaires",
    especes: "Espèces physiques",
    comptoirs: "Caisses comptoir",
    poste: (m) => `posté ${m}`,
    ventesEnCours: (m) => `+ ventes espèces en cours ${m}`,
    nbComptes: (n) => (n === 1 ? "1 compte" : `${n} comptes`),
    dontVirtuelles: (m) => `dont affecté aux caisses virtuelles ${m}`,
    dontCoffre: (m) => `dont coffre ${m}`,
    sessionsOuvertes: (n) => (n === 0 ? "aucune session ouverte" : n === 1 ? "1 session ouverte" : `${n} sessions ouvertes`),
    dontVentes: (m) => `dont ventes en cours ${m}`,
    filtrer: (libelle) => `Afficher seulement : ${libelle}`,
  }),

  // ── FilterBar ──
  recherche: "Rechercher un compte…",
  rechercheLabel: "Rechercher un compte par son nom ou son code",
  filtreType: "Type",
  tous: "Tous",
  inactifs: "Afficher les comptes inactifs",
  misAJour: (heure) => `Mis à jour ${heure}`,
  reinitialiser: "Réinitialiser les filtres",

  // ── Grid (UX 02 §4.2) ──
  grille: "Comptes et caisses de l'officine",
  colonnes: Object.freeze({
    nom: "Nom",
    type: "Type",
    confirme: "Confirmé (DA)",
    previsionnel: "Prévisionnel (DA)",
    etat: "État",
    dernier: "Dernier mouvement",
    mois: "Entrées · Sorties (mois)",
  }),
  nonAffecte: "Non affecté",
  caisseCentrale: "Caisse centrale",
  poste: (nom) => `Poste ${nom}`,
  sansPoste: "Aucun poste lié",
  comptoirLigne: Object.freeze({
    poste: "posté",
    ventes: "ventes",
    attendu: "attendu",
    sessionOuverte: (qui, heure) => `Session ouverte · ${qui} depuis ${heure}`,
    fermee: "Aucune session ouverte",
  }),
  masque: "Masqué pour votre rôle",
  inconnu: "Montant inconnu",
  pied: Object.freeze({
    poste: "Trésorerie de l'officine (posté)",
    ventes: "+ Ventes espèces des sessions en cours",
    liquidite: "= Liquidité totale",
    groupe: (libelle) => `Total ${libelle.toLowerCase()}`,
    comptes: (n) => (n === 1 ? "1 compte" : `${n} comptes`),
    recherche: (n) => `${n === 1 ? "1 compte affiché" : `${n} comptes affichés`} — les totaux portent sur toute l'officine`,
  }),

  // ── States (UX 02 §4.6) ──
  vide: Object.freeze({
    titre: "Déclarez vos comptes bancaires, vos caisses et vos comptoirs pour voir toute la liquidité de l'officine.",
    banque: "Nouveau compte bancaire",
    physique: "Nouvelle caisse physique",
    comptoir: "Nouvelle caisse comptoir",
  }),
  aucunResultat: (q) => `Aucun compte ne correspond à « ${q} ».`,
  aucunResultatType: "Aucun compte de ce type.",
  erreur: "Les comptes n'ont pas pu être chargés.",
  interdit: "Votre rôle ne donne pas accès aux comptes de la trésorerie.",

  // ── « Nouveau… » (F3) ──
  nouveau: "Nouveau…",
  nouveauMenu: Object.freeze({
    BANQUE: "Compte bancaire",
    ESPECES: "Caisse physique",
    VIRTUEL: "Caisse virtuelle",
    COMPTOIR: "Caisse comptoir",
  }),

  // ── Row menu (UX 02 §4.5) ──
  actions: Object.freeze({
    ouvrir: "Ouvrir le compte",
    nouvelleVirtuelle: "Nouvelle caisse virtuelle",
    modifier: "Modifier le compte",
    desactiver: "Désactiver le compte…",
    reactiver: "Réactiver le compte",
    journee: "Voir la journée du comptoir",
    // TWP-20 — the movement gestures (the one OperationForm, UX 02 §3)
    mouvement: "Nouveau mouvement sur ce compte",
    transferer: "Transférer depuis ce compte",
    affecter: "Affecter à une caisse virtuelle",
    // The drawer footer's short words (the tooltip and the F1 sheet carry the full sentence).
    mouvementCourt: "Nouveau mouvement",
    transfererCourt: "Transférer",
    affecterCourt: "Affecter",
  }),
  raisons: Object.freeze({
    soldeNonNul: (m) => `Solde non nul : transférez d'abord ${m}`,
    enfantActif: "Une caisse virtuelle active en dépend : désactivez-la d'abord.",
    sessionOuverte: "Une session est ouverte sur ce comptoir : clôturez-la d'abord.",
    centrale: "La caisse centrale de l'officine ne se désactive pas : désignez-en une autre d'abord.",
    parentInactif: "Sa caisse parente est inactive : réactivez-la d'abord.",
  }),
  desactivation: Object.freeze({
    titre: (libelle) => `Désactiver « ${libelle} » ?`,
    message: "Le compte disparaît des listes de saisie ; son historique reste consultable. Un compte n'est jamais supprimé.",
    confirmer: "Désactiver le compte",
    fait: (libelle) => `Compte désactivé : ${libelle}`,
    reactive: (libelle) => `Compte réactivé : ${libelle}`,
  }),
});

/** UX 02 §1.4 — the état badges of a row (registry `compte_tresorerie` / `session_caisse`). */
export const ETATS_COMPTE = Object.freeze({
  ACTIF: "ACTIF",
  INACTIF: "INACTIF",
  SOUS_LE_SEUIL: "SOUS_LE_SEUIL",
  SOLDE_NEGATIF: "SOLDE_NEGATIF",
});

/** C2 — the account drawer (UX 02 §5). */
export const TIROIR_COMPTE = Object.freeze({
  fermer: "Fermer (Échap)",
  precedent: "Compte précédent",
  suivant: "Compte suivant",
  chargement: "Chargement du compte…",
  erreur: "Ce compte n'a pas pu être chargé.",
  tuiles: Object.freeze({
    solde: "Solde",
    disponible: "Disponible",
    previsionnel: "Prévisionnel",
    totalPhysique: "Total physique",
    totalPhysiqueAide: "= non affecté + virtuelles",
    nonAffecte: "Non affecté",
    nonAffecteAide: "utilisable pour sortir",
    partDuParent: (parent) => `Part de ${parent}`,
    especesAttendues: "Espèces attendues",
    especesAttenduesAide: "posté + ventes en cours",
    poste: "Posté",
    ventesEnCours: "Ventes espèces en cours",
    aucunBrouillon: "aucun brouillon",
    avecBrouillons: "brouillons compris",
    dernierMouvement: "Dernier mouvement",
    aucunMouvement: "aucun mouvement",
  }),
  rib: (rib) => `RIB ${rib}`,
  decouvert: (m) => `Découvert autorisé : ${m}`,
  sansDecouvert: "Découvert autorisé : —",
  seuil: (m) => `Seuil d'alerte : ${m}`,
  creeLe: (date, qui) => (qui ? `Créé le ${date} par ${qui}` : `Créé le ${date}`),
  virtuellesLigne: "Caisses virtuelles :",
  regleVirtuelle: (enfant, parent) =>
    `L'argent de ${enfant} est rangé dans ${parent}. Il n'est jamais déplacé automatiquement : chaque affectation est un transfert enregistré.`,
  sessionOuverte: (qui, heure) => `Session ouverte · ${qui} depuis ${heure}`,
  aucuneSession: (fonds) => (fonds ? `Aucune session ouverte — fonds retenu ${fonds}` : "Aucune session ouverte"),
  onglets: Object.freeze({
    mouvements: "Mouvements",
    flux: "Entrées / Sorties",
    transferts: "Transferts",
    virtuelles: "Virtuelles",
    journee: "Journée",
    sessions: "Sessions",
    parametres: "Paramètres",
  }),
  ongletsLabel: "Sections du compte",
  ecritures: Object.freeze({
    // TWP-29 — UX 03 §11.2 « Voir tout › »: the account's « Mouvements de caisse » in the journal.
    voirJournal: "Voir tous les mouvements dans le journal",
    colonnes: Object.freeze({
      date: "Date",
      numero: "N°",
      sens: "Sens",
      libelle: "Motif / tiers",
      montant: "Montant (DA)",
      solde: "Solde (DA)",
    }),
    grille: (libelle) => `Mouvements de ${libelle}`,
    vide: "Aucun mouvement sur ce compte.",
    plus: "Afficher les mouvements plus anciens",
    vers: (autre) => `⟶ ${autre}`,
    depuis: (autre) => `⟵ ${autre}`,
    horsTresorerie: "Hors trésorerie",
    compensation: "contre-passation",
    soldeMasque: "Le solde courant est masqué pour votre rôle.",
  }),
  flux: Object.freeze({
    grille: "Entrées et sorties par période",
    colonnes: Object.freeze({ periode: "Période", entrees: "Entrées (DA)", sorties: "Sorties (DA)", solde: "Solde fin (DA)" }),
    vide: "Aucun mouvement sur la période.",
    periode: "Période",
  }),
  transferts: Object.freeze({
    grille: "Transferts du compte",
    colonnes: Object.freeze({ date: "Date", numero: "N°", sens: "Sens", autre: "Autre compte", motif: "Motif", auteur: "Par", montant: "Montant (DA)", statut: "État" }),
    vide: "Aucun transfert sur ce compte.",
    recu: "Reçu",
    emis: "Émis",
    plus: "Afficher les transferts plus anciens",
  }),
  virtuelles: Object.freeze({
    grille: "Caisses virtuelles",
    colonnes: Object.freeze({ nom: "Caisse virtuelle", solde: "Solde (DA)", previsionnel: "Prévisionnel (DA)", dernier: "Dernier mouvement", etat: "État" }),
    vide: "Aucune caisse virtuelle dans cette caisse.",
    nouvelle: "Nouvelle caisse virtuelle",
  }),
  parametres: Object.freeze({
    code: "Code",
    libelle: "Libellé",
    type: "Type",
    banque: "Banque",
    rib: "RIB",
    seuil: "Seuil d'alerte de solde",
    decouvert: "Découvert autorisé",
    note: "Note",
    poste: "Poste de caisse",
    parent: "Caisse physique parente",
    caisseCentrale: "Caisse centrale de l'officine",
    oui: "Oui",
    non: "Non",
    ouverture: "Solde d'ouverture",
    ouvertureLigne: (m, date, numero) => `${m} le ${date}${numero ? ` — ${numero}` : ""}`,
    aucuneOuverture: "Aucun solde d'ouverture.",
    lectureSeule: "Lecture seule : seul le propriétaire modifie un compte.",
  }),
});

/** C3-C6 — the account forms (UX 02 §6-§7, §9.2). */
export const FORMULAIRE_COMPTE = Object.freeze({
  titres: Object.freeze({
    BANQUE: "Nouveau compte bancaire",
    ESPECES: "Nouvelle caisse physique",
    COFFRE: "Nouvelle caisse physique",
    VIRTUEL: "Nouvelle caisse virtuelle",
    COMPTOIR: "Nouvelle caisse comptoir",
  }),
  titresModification: Object.freeze({
    BANQUE: "Modifier le compte bancaire",
    ESPECES: "Modifier la caisse physique",
    COFFRE: "Modifier le coffre",
    VIRTUEL: "Modifier la caisse virtuelle",
    COMPTOIR: "Modifier la caisse comptoir",
  }),
  alertes: Object.freeze({
    BANQUE: "Un compte bancaire représente de l'argent réel détenu dans une banque. Son solde évolue uniquement par des mouvements enregistrés et traçables.",
    VIRTUEL: (parent) =>
      `Une caisse virtuelle est une enveloppe DANS ${parent} : son argent est physiquement dans ${parent}. Encaisser directement dans la caisse virtuelle augmente aussi le total physique de ${parent}. Encaisser dans ${parent} n'augmente AUCUNE caisse virtuelle : pour affecter l'argent, faites un transfert.`,
    COMPTOIR: "Une caisse comptoir est le tiroir d'un poste de vente : ses sessions de caisse s'ouvrent et se clôturent dessus.",
  }),
  sections: Object.freeze({
    identification: "Identification",
    ouverture: "Solde d'ouverture",
    controle: "Contrôle",
  }),
  champs: Object.freeze({
    banque: "Banque",
    libelleBanque: "Libellé du compte",
    libelle: "Libellé",
    code: "Code",
    codePropose: "proposé à partir du libellé",
    rib: "RIB",
    nature: "Nature",
    natureCaisse: "Caisse",
    natureCoffre: "Coffre",
    caisseCentrale: "Caisse centrale de l'officine",
    caisseCentraleAide: "Reçoit les versements des comptoirs à la clôture. Une seule caisse centrale par officine.",
    caisseCentraleActuelle: (libelle) => `Actuellement : ${libelle}. Cocher cette case la remplacera.`,
    parent: "Caisse physique parente",
    parentAide: "La caisse physique dont cette enveloppe fait partie.",
    parentVerrouille: "La caisse parente ne peut plus changer : cette caisse a déjà des mouvements.",
    poste: "Poste de caisse",
    posteAide: "Facultatif. Si un poste est lié, la session s'ouvre sur ce comptoir depuis ce poste.",
    aucunPoste: "Aucun poste",
    posteLie: (comptoir) => `lié à ${comptoir}`,
    montantInitial: "Montant initial",
    soldeInitial: "Solde initial",
    montantDepart: "Montant de départ",
    fondsCaisse: "Fonds de caisse",
    dateSolde: "Date du solde",
    dateSoldeAide: "Aujourd'hui : un compte n'a pas de mouvement avant sa création.",
    provenance: "Provenance",
    source: "Compte d'origine",
    sourceAide: "Le compte de l'officine d'où vient le montant.",
    seuil: "Seuil d'alerte de solde",
    seuilAide: "Facultatif",
    decouvert: "Découvert autorisé",
    decouvertAide: "Vide = aucun découvert",
    note: "Note",
    fondsRetenu: "Fonds retenu à la clôture",
    fondsRetenuLigne: (m) => `${m} — réglé dans Paramètres de trésorerie`,
    aideMontantInitial:
      "Pharmakon tiendra ensuite le solde à jour à partir des mouvements de cette caisse. Le montant initial ne pourra plus être modifié qu'en corrigeant son opération.",
  }),
  provenances: Object.freeze({
    BANQUE: Object.freeze({ SOLDE_INITIAL: "Solde du relevé bancaire (argent déjà en banque)", TRANSFERT: "Versé depuis un compte de l'officine" }),
    ESPECES: Object.freeze({ SOLDE_INITIAL: "Espèces déjà présentes, non encore enregistrées (solde initial)", TRANSFERT: "Prises sur un compte de l'officine (transfert)" }),
    COFFRE: Object.freeze({ SOLDE_INITIAL: "Espèces déjà présentes, non encore enregistrées (solde initial)", TRANSFERT: "Prises sur un compte de l'officine (transfert)" }),
    VIRTUEL: Object.freeze({ TRANSFERT: (parent) => `Affecté depuis ${parent} (non affecté)`, SOLDE_INITIAL: "Argent nouveau (solde initial)" }),
    COMPTOIR: Object.freeze({ TRANSFERT: "Pris dans la caisse centrale (transfert)", SOLDE_INITIAL: "Espèces nouvelles (solde initial)" }),
  }),
  apercu: Object.freeze({
    titre: "Aperçu",
    totalPhysique: "Total physique",
    nonAffecte: "Non affecté",
    inchange: "(inchangé)",
    calcul: "Calcul de l'aperçu…",
    indisponible: "L'aperçu n'a pas pu être calculé ; la création reste vérifiée par le serveur.",
  }),
  resume: Object.freeze({
    avecOuverture: (libelle, m, soldeInitial) =>
      `Résumé : ${libelle} sera créé avec un solde de ${m}${soldeInitial ? " (solde initial, hors recettes)" : " (transfert depuis un compte de l'officine)"}.`,
    sansOuverture: (libelle) => `Résumé : ${libelle} sera créé avec un solde de 0,00 DA.`,
  }),
  ouvertureExistante: (m, date, numero) => `Solde d'ouverture : ${m} le ${date}${numero ? ` — ${numero}` : ""}`,
  ouvertureCorrection: "Le solde d'ouverture se corrige en corrigeant son opération.",
  boutons: Object.freeze({
    annuler: "Annuler",
    creer: "Créer le compte",
    creerCaisse: "Créer la caisse",
    enregistrer: "Enregistrer",
  }),
  confirmation: Object.freeze({
    titre: (libelle, m) => `Créer le compte « ${libelle} » avec un solde initial de ${m} ?`,
    titreTransfert: (libelle, m, source) => `Créer « ${libelle} » avec ${m} pris sur « ${source} » ?`,
    message: (date) => `Le montant d'ouverture est enregistré comme une opération confirmée du ${date} ; il n'est pas compté dans les recettes.`,
    confirmer: "Créer le compte",
  }),
  succes: Object.freeze({
    // « Compte bancaire créé », « Caisse … créée »: the agreement follows the head noun.
    cree: (nom, libelle, m) => `${nom} ${/^caisse/i.test(nom) ? "créée" : "créé"} : ${libelle}${m ? ` (${m})` : ""}`,
    modifie: (libelle) => `Compte modifié : ${libelle}`,
    ouvrir: "Ouvrir le compte",
  }),
  erreurs: Object.freeze({
    resume: (n) => (n === 1 ? "1 champ à corriger" : `${n} champs à corriger`),
    requis: (champ) => `${champ} requis`,
    banque: "Indiquez la banque",
    rib: "Indiquez le RIB",
    parent: "Choisissez la caisse physique parente",
    source: "Choisissez le compte d'où vient le montant",
    conflit: "Ce compte a été modifié entre-temps. Rechargez-le avant d'enregistrer.",
    recharger: "Recharger",
  }),
});

/** C6 — the « Journée » of a comptoir (UX 02 §9.4). */
export const JOURNEE = Object.freeze({
  titre: (comptoir) => `${comptoir} · Journée`,
  jourPrecedent: "Jour précédent",
  jourSuivant: "Jour suivant",
  aujourdhui: "Aujourd'hui",
  session: (heure, qui, fin) => `Session · ${qui} · ${heure} → ${fin}`,
  sessionOuverte: "ouverte",
  aucuneSession: "Aucune session ce jour-là.",
  aucuneLigne: "Aucun mouvement ce jour-là.",
  recalcule: "recalculé",
  resume: Object.freeze({
    titre: "Résumé",
    fonds: "Fonds à l'ouverture",
    ecartOuverture: "dont écart d'ouverture",
    ventes: "Ventes espèces",
    entrees: "Entrées",
    sorties: "Sorties",
    recus: "Transferts reçus",
    emis: "Transferts émis",
    autres: "Autres écritures du comptoir",
    attendues: "= Espèces attendues",
    parMode: "Ventes par mode",
    mixte: "Mixte (non ventilé)",
    relation: "Les ventes en espèces sont versées au solde du comptoir à la clôture.",
    cloture: (heure) => `Clôture ${heure}`,
    attendu: "attendu",
    compte: "compté",
    fondsRetenu: "fonds retenu",
    versement: "versé à la caisse centrale",
  }),
  colonnes: Object.freeze({
    heure: "Heure",
    evenement: "Événement",
    par: "Par",
    flux: "Depuis ⟶ Vers",
    montant: "Montant (DA)",
    etat: "État",
  }),
  grille: "Événements de la journée",
  evenements: Object.freeze({
    ouverture: (m) => `Ouverture (fonds ${m})`,
    cloture: "Clôture de la session",
    ventes: (n) => `Ventes (${n}) — détail ›`,
    annulation: (numero) => `Annulation${numero ? ` de ${numero}` : ""}`,
  }),
  horsTresorerie: "Hors trésorerie",
  modifiee: "Modifiée",
  tronque: (n) => `Les ${n} premières opérations de la journée sont listées ; le résumé porte sur toute la session.`,
  voirVentes: "Voir les ventes",
  rapportZ: "Rapport Z",
  rapportX: "Rapport X",
});

/** C6 — « Sessions de caisse » (UX 02 §9.5) and the session drawer. */
export const SESSIONS = Object.freeze({
  grille: "Sessions de caisse",
  colonnes: Object.freeze({
    session: "Session",
    comptoir: "Comptoir",
    caissier: "Caissier",
    ouverte: "Ouverte",
    fermee: "Fermée",
    attendues: "Espèces attendues (DA)",
    comptees: "Comptées (DA)",
    ecart: "Écart",
    etat: "État",
    ventes: "Ventes",
    ca: "CA total (DA)",
  }),
  filtres: Object.freeze({
    comptoir: "Comptoir",
    caissier: "Caissier",
    periode: "Période",
    statut: "État",
    avecEcart: "Avec écart",
    ecart: "Écart",
  }),
  pied: Object.freeze({
    ecarts: "Σ écarts figés (filtre actif)",
    sansInstantane: (n) =>
      n === 1 ? "1 session close avant la clôture atomique : écart recalculé, non compté" : `${n} sessions closes avant la clôture atomique : écarts recalculés, non comptés`,
  }),
  pagination: (debut, fin, total) => `${debut}–${fin} sur ${total}`,
  pagePrecedente: "Précédent",
  pageSuivante: "Suivant",
  page: (p, n) => `Page ${p} / ${n}`,
  vide: "Aucune session de caisse.",
  videFiltre: "Aucune session ne correspond aux filtres.",
  sansComptoir: "sans comptoir",
  ouvrir: "Ouvrir une session",
  cloturerMienne: "Clôturer ma session",
  exporter: "Exporter",
  actions: Object.freeze({
    ouvrir: "Voir la session",
    cloturer: "Clôturer la session",
    rouvrir: "Rouvrir la session…",
    ajuster: "Mouvement d'espèces…",
    rapportZ: "Rapport Z",
    rapportX: "Rapport X",
    journee: "Journée du comptoir",
  }),
  reouverture: Object.freeze({
    titre: "Rouvrir cette session ?",
    message: "Les écritures de la clôture sont annulées (contre-passées) ; la prochaine clôture les écrit de nouveau, une seule fois.",
    motif: "Motif de la réouverture",
    confirmer: "Rouvrir la session",
    fait: "Session rouverte",
  }),
  tiroir: Object.freeze({
    titre: (comptoir) => `Session · ${comptoir}`,
    onglets: Object.freeze({ resume: "Résumé", journal: "Journal" }),
    ongletsLabel: "Sections de la session",
    caissier: "Caissier",
    ouverte: "Ouverte",
    fermee: "Fermée",
    ouvreur: "Ouverte par",
    clotureur: "Clôturée par",
    fondsOuverture: "Fonds à l'ouverture",
    ventesEspeces: "Ventes espèces",
    mouvements: "Mouvements du tiroir",
    attendues: "Espèces attendues",
    comptees: "Comptées",
    ecart: "Écart",
    versement: "Versé à la caisse centrale",
    fondsRetenu: "Fonds retenu",
    notes: "Notes de clôture",
    parMode: "Ventes par mode de paiement",
    aucuneVente: "Aucune vente.",
    journalVentes: "Ventes",
    encaissements: "Encaissements",
    decaissements: "Décaissements",
    ajustements: "Ajustements de fonds",
    erreur: "La session n'a pas pu être chargée.",
    masqueAveugle: "Comptage à l'aveugle : le montant attendu n'est pas affiché avant la clôture.",
  }),
});
